import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AnalyticsEntry } from 'app/api/analytics.api.service';
import { RuleEntry } from 'app/api/rule.api.service';
import { selectCustomerRule, selectProviderRule } from 'app/main/rule/rule.selectors';
import { getError, LoadingState } from 'app/utils/callstate';
import * as _ from 'lodash';

import { State, RouteState, fromAnalytics } from 'app/main/analytics/reducers';
import { AnalyticsQuery, AnalyticsState } from 'app/main/analytics/analytics.types';
import { selectReportSelectedGroupKey } from './analytics.ui.selectors';

export const getRouteState = createFeatureSelector<State, RouteState>('analytics');

export const selectAnalyticsState = createSelector(
    getRouteState,
    ({ api }): AnalyticsState => api
);

export const selectAnalytics = createSelector(
    selectAnalyticsState,
    fromAnalytics.selectAll
);

export const selectAnalyticsByAxis = axis => createSelector(
    selectAnalytics,
    entries => _.filter(entries, { axis }) as AnalyticsEntry[]
);

/** Select ANA1 analytics entries sorted by sortOrder */
export const selectAna1 = createSelector(
    selectAnalyticsByAxis('ana1'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
/** Select ANA2 analytics entries sorted by sortOrder */
export const selectAna2 = createSelector(
    selectAnalyticsByAxis('ana2'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
/** Select ANA3 analytics entries sorted by sortOrder */
export const selectAna3 = createSelector(
    selectAnalyticsByAxis('ana3'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
export const selectGroups = createSelector(
    selectAnalyticsByAxis('group'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
export const selectCGroups = createSelector(
    selectAnalyticsByAxis('cgroup'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
export const selectPGroups = createSelector(
    selectAnalyticsByAxis('pgroup'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
export const selectCCat = createSelector(
    selectAnalyticsByAxis('ccat'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
export const selectPCat = createSelector(
    selectAnalyticsByAxis('pcat'),
    entries => _.sortBy(entries, 'sortOrder') as AnalyticsEntry[]
);
export const selectCustomers = createSelector(
    selectAnalyticsByAxis('cust'),
    entries => _.sortBy(entries, 'name') as AnalyticsEntry[]
);
export const selectProviders = createSelector(
    selectAnalyticsByAxis('prov'),
    entries => _.sortBy(entries, 'name') as AnalyticsEntry[]
);

export const selectAna1WithCount = createSelector(
    selectAna1,
    selectAna2,
    (ana1, ana2) => {
        return ana1.map((entry: AnalyticsEntry) => {
            const countAna2 = _.countBy(ana2, { parentKey: entry.key });
            return { ...entry, countAna2: countAna2.true };
        });
    }
);

export const selectAna2WithAna1CountAndGroups = createSelector(
    selectAna1,
    selectAna2,
    selectAna3,
    (ana1, ana2, ana3) => {
        return ana2.map((ana2Entry: AnalyticsEntry) => {
            const ana1Entry = ana1.find(el => el.key === ana2Entry.parentKey);
            const countAna3 = _.countBy(ana3, { parentKey: ana2Entry.key });
            const returnValue = {
                ...ana2Entry,
                ana1KeyValue: `${ana1Entry.key}|${ana1Entry.name}`,
                countAna3: countAna3.true
            };
            if (ana2Entry.groups) {
                ana2Entry.groups.forEach(groupKey => {
                    returnValue[groupKey] = true;
                });
            }
            return returnValue;
        });
    }
);

export const selectFlatGroups = createSelector(
    selectGroups,
    groups => groups.map(group => {
        return {
            companyId: group.companyId,
            path: `${group.id}`,
            id: group.id,
            key: group.key,
            sortOrder: group.sortOrder,
            name: group.name,
            color: group.color
        };
    })
);
export const selectFlatAna2Groups = createSelector(
    selectAna2,
    selectGroups,
    (ana2, group) => {
        const flatAna2 = [];
        ana2.forEach(a => {
            if (a.groups && a.groups.length > 0) {
                a.groups.forEach(groupKey => {
                    const groupElement = group.find(el => el.key === groupKey);
                    flatAna2.push({
                        companyId: a.companyId,
                        path: `${groupElement.id}#${a.id}`,
                        id: a.id,
                        key: a.key,
                        sortOrder: a.sortOrder,
                        name: a.name,
                    });
                });
            }
        });
        return flatAna2;
    }
);
export const selectFlatAna3Groups = createSelector(
    selectFlatAna2Groups,
    selectAna3,
    (ana2, ana3) => {
        const flatAna3 = [];
        ana2.forEach(a2 => {
            const ana3List = ana3.filter(a3 => a3.parentKey === a2.key);
            ana3List.forEach(el => {
                flatAna3.push({
                    companyId: el.companyId,
                    path: `${a2.path}#${el.id}`,
                    id: el.id,
                    key: el.key,
                    sortOrder: el.sortOrder,
                    name: el.name,
                });
            });
        });
        return flatAna3;
    }
);
export const selectAnalyticsTreeGroups2 = createSelector(
    selectFlatGroups,
    selectFlatAna2Groups,
    (groups, ana2) => {
        const tree1 = groups.map(a => {
            return {
                companyId: a.companyId,
                key: a.key,
                axis: 'group',
                id: a.id,
                name: a.name,
                path: a.path,
                sortOrder: a.sortOrder,
                color: a.color,
            };
        });
        const tree2 = ana2.map(a => {
            return {
                companyId: a.companyId,
                key: a.key,
                axis: 'ana2',
                id: a.id,
                name: a.name,
                path: a.path,
                sortOrder: a.sortOrder,
            };
        });
        return _.concat(tree1, tree2);
    }
);
export const selectAnalyticsTreeGroups3 = createSelector(
    selectAnalyticsTreeGroups2,
    selectFlatAna3Groups,
    (groups, ana3) => {
        const tree3 = ana3.map(a => {
            return {
                companyId: a.companyId,
                key: a.key,
                axis: 'ana3',
                id: a.id,
                name: a.name,
                path: a.path,
                sortOrder: a.sortOrder,
            };
        });
        return _.concat(groups, tree3);
    }
);

export const selectFlatAna1 = createSelector(
    selectAna1,
    (ana1) => {
        return ana1.map(ana1Entry => {
            return {
                companyId: ana1Entry.companyId,
                path: `${ana1Entry.id}`,
                ana1Id: ana1Entry.id,
                ana1Key: ana1Entry.key,
                ana1SortOrder: ana1Entry.sortOrder,
                ana1Name: ana1Entry.name,
            };
        });
    }
);
export const selectFlatAna2 = createSelector(
    selectAna1,
    selectAna2,
    (ana1, ana2) => {
        return ana2.map(ana2Entry => {
            const ana1Entry = ana1.find(el => el.key === ana2Entry.parentKey);
            return {
                companyId: ana2Entry.companyId,
                path: `${ana1Entry.id}#${ana2Entry.id}`,
                ana1Key: ana1Entry.key,
                ana2Key: ana2Entry.key,
                ana1Id: ana1Entry.id,
                ana2Id: ana2Entry.id,
                ana1SortOrder: ana1Entry.sortOrder,
                ana2SortOrder: ana2Entry.sortOrder,
                ana1Name: ana1Entry.name,
                ana2Name: ana2Entry.name,
            };
        });
    }
);
export const selectFlatAna3 = createSelector(
    selectAna1,
    selectAna2,
    selectAna3,
    (ana1, ana2, ana3) => {
        return ana3.map(ana3Entry => {
            const ana2Entry = ana2.find(el => el.key === ana3Entry.parentKey);
            const ana1Entry = ana1.find(el => el.key === ana2Entry.parentKey);
            return {
                companyId: ana3Entry.companyId,
                path: `${ana1Entry.id}#${ana2Entry.id}#${ana3Entry.id}`,
                ana1Key: ana1Entry.key,
                ana2Key: ana2Entry.key,
                ana3Key: ana3Entry.key,
                ana1Id: ana1Entry.id,
                ana2Id: ana2Entry.id,
                ana3Id: ana3Entry.id,
                ana1SortOrder: ana1Entry.sortOrder,
                ana2SortOrder: ana2Entry.sortOrder,
                ana3SortOrder: ana3Entry.sortOrder,
                ana1Name: ana1Entry.name,
                ana2Name: ana2Entry.name,
                ana3Name: ana3Entry.name,
                paymentPeriod: ana3Entry.paymentPeriod,
                isCash: ana3Entry.isCash,
            };
        });
    }
);
export const selectAnalyticsTree = createSelector(
    selectFlatAna1,
    selectFlatAna2,
    selectFlatAna3,
    (ana1, ana2, ana3) => {
        const tree1 = ana1.map(a => {
            return {
                companyId: a.companyId,
                key: a.ana1Key,
                axis: 'ana1',
                id: a.ana1Id,
                name: a.ana1Name,
                path: a.path,
                sortOrder: a.ana1SortOrder,
            };
        });
        const tree2 = ana2.map(a => {
            return {
                companyId: a.companyId,
                key: a.ana2Key,
                axis: 'ana2',
                id: a.ana2Id,
                name: a.ana2Name,
                path: a.path,
                sortOrder: a.ana2SortOrder,
            };
        });
        const tree3 = ana3.map(a => {
            return {
                companyId: a.companyId,
                key: a.ana3Key,
                axis: 'ana3',
                id: a.ana3Id,
                name: a.ana3Name,
                path: a.path,
                sortOrder: a.ana3SortOrder,
            };
        });
        return _.concat(tree1, tree2, tree3);
    }
);
export const selectAna2ForReportSelectedGroup = createSelector(
    selectAna2,
    selectReportSelectedGroupKey,
    (ana2, groupKey) => ana2.filter(a => a.groups && a.groups.findIndex(item => item === groupKey) > -1)
);

export const selectCust = createSelector(
    selectCCat,
    selectCustomers,
    selectCustomerRule,
    (ccat, cust, rule) => {
        return cust.map((entry: AnalyticsEntry) => {
            const countRules = _.countBy(rule, { anaKey: entry.key });
            const returnValue = { ...entry, countRules: countRules.true, category: '', ccatKeyValue: '' };
            if (entry.parentKey) {
                const { key, name } = ccat.find(el => el.key === entry.parentKey);
                returnValue.ccatKeyValue = `${key}|${name}`;
                returnValue.category = name;
            }
            if (entry.groups) {
                entry.groups.forEach(groupKey => {
                    returnValue[groupKey] = true;
                });
            }
            return returnValue;
        });
    }
);
export const selectProv = createSelector(
    selectPCat,
    selectProviders,
    selectProviderRule,
    (pcat, prov, rule) =>
        prov.map((entry: AnalyticsEntry) => {
            const countRules = _.countBy(rule, { anaKey: entry.key });
            const returnValue = { ...entry, countRules: countRules.true, category: '', pcatKeyValue: '' };
            if (entry.parentKey) {
                const { key, name } = pcat.find(el => el.key === entry.parentKey);
                returnValue.pcatKeyValue = `${key}|${name}`;
                returnValue.category = name;
            }
            if (entry.groups) {
                entry.groups.forEach(groupKey => {
                    returnValue[groupKey] = true;
                });
            }
            return returnValue;
        })
);


export const selectCcat = createSelector(
    selectCCat,
    selectCustomers,
    (ccat, cust) => ccat.map((entry: AnalyticsEntry) => {
        const countCust = _.countBy(cust, { parentKey: entry.key });
        return { ...entry, countCust: countCust.true };
    })
);
export const selectPcat = createSelector(
    selectPCat,
    selectProviders,
    (pcat, prov) => pcat.map((entry: AnalyticsEntry) => {
        const countProv = _.countBy(prov, { parentKey: entry.key });
        return { ...entry, countProv: countProv.true };
    })
);

export const selectFlatCustomerRule = createSelector(
    selectCustomerRule,
    selectCust,
    (rule, cust) => _.sortBy(rule.map((entry: RuleEntry) => {
        const returnValue = { ...entry, customerLabel: '', customerCategory: '', custKeyValue: '' };
        if (entry.anaKey) {
            const { key, name, category } =
                cust.find(c => c.key === entry.anaKey) || { key: '', name: '', category: '' };
            returnValue.customerLabel = name;
            returnValue.customerCategory = category;
            returnValue.custKeyValue = key ? `${key}|${name}` : '';
        }
        return returnValue;
    }), 'name')
);
export const selectFlatProviderRule = createSelector(
    selectProviderRule,
    selectProv,
    (rule, prov) => _.sortBy(rule.map((entry: RuleEntry) => {
        const returnValue = { ...entry, providerLabel: '', providerCategory: '', provKeyValue: '' };
        if (entry.anaKey) {
            const { key, name, category } =
                prov.find(c => c.key === entry.anaKey) || { key: '', name: '', category: '' };
            returnValue.providerLabel = name;
            returnValue.providerCategory = category;
            returnValue.provKeyValue = key ? `${key}|${name}` : '';
        }
        return returnValue;
    }), 'name')
);

export const selectAnaGroups = createSelector(
    selectGroups,
    selectAna2,
    (groups, ana2) => {
        return groups.map((entry: AnalyticsEntry) => {
            const countAna2 = _.countBy(ana2, ana2Entry =>
                ana2Entry.groups && ana2Entry.groups.indexOf(entry.key) > -1);
            return { ...entry, countAna2: countAna2.true, color: entry.color || '-' };
        });
    }
);
export const selectFlatAnaGroups = createSelector(
    selectGroups,
    selectAna2,
    (groups, ana2) => {

        return ana2.map((entry: AnalyticsEntry) => {
            if (entry.groups) {
                entry.groups.forEach(key => {
                    const { name } = groups.find(el => el.key === key);
                    entry[name] = true;
                });
            }
            return { ...entry };
        });
    }
);

export const selectCustGroups = createSelector(
    selectCGroups,
    selectCustomers,
    (groups, cust) => {
        return groups.map((entry: AnalyticsEntry) => {
            const countCust = _.countBy(cust, custEntry =>
                custEntry.groups && custEntry.groups.indexOf(entry.key) > -1);
            return { ...entry, countCust: countCust.true };
        });
    }
);
export const selectProvGroups = createSelector(
    selectPGroups,
    selectProviders,
    (groups, prov) => {
        return groups.map((entry: AnalyticsEntry) => {
            const countProv = _.countBy(prov, provEntry =>
                provEntry.groups && provEntry.groups.indexOf(entry.key) > -1);
            return { ...entry, countProv: countProv.true };
        });
    }
);

export const selectAnalyticsLoading = createSelector(
    selectAnalyticsState,
    analyticsState => analyticsState.callState === LoadingState.Loading
);

export const selectAnalyticsLoaded = createSelector(
    selectAnalyticsState,
    analyticsState => analyticsState.callState === LoadingState.Loaded
);

export const selectAnalyticsError = createSelector(
    selectAnalyticsState,
    analyticsState => getError(analyticsState.callState)
);

export const selectAnalyticsQuery = createSelector(
    selectAnalyticsState,
    analyticsState => ({
        companyId: analyticsState.companyId,
        key: analyticsState.key,
        filter: analyticsState.filter
    } as AnalyticsQuery)
);

export const selectAnalyticsCompanyId = createSelector(
    selectAnalyticsState,
    analyticsState => analyticsState.companyId
);

